/**
 * North Star Decision Intelligence OS
 * API Service Layer
 * 
 * Version: 3H-A.1
 * Fixed: AdminPoolService count->total mapping, added delete
 */

import { 
  Pool, PoolMembership, LeaderboardEntry, BSLProfile,
  CreatePoolInput, UpdatePoolInput, BalanceAdjustmentInput, getAppConfig,
  Event, Prediction, CreatePredictionRequest, Portfolio, CreatePortfolioRequest,
  PickSlateResponse, SubmitPickRequest, PickResponse,
  GMSlateResponse, SubmitPortfolioRequest, PortfolioResponse,
  AdminStats, AdminPool, AdminPoolListResponse, AdminMembershipListResponse,
  AdjustBalanceRequest, AdjustBalanceResponse, RemoveMemberRequest, RemoveMemberResponse,
  AuditLogEntry, AuditLogListResponse, AdminCreatePoolRequest, 
  AdminUpdatePoolRequest, LockPoolRequest
} from './types';
const config = getAppConfig();

// =============================================================================
// API Client Class
// =============================================================================

class ApiClient {
  private baseUrl: string;
  private userId: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setUserId(userId: string) {
    this.userId = userId;
    localStorage.setItem('current_user_id', userId);
  }

  getUserId(): string | null {
  // Always read from localStorage to stay in sync with auth state
  this.userId = localStorage.getItem('current_user_id');
  return this.userId;
}

  clearUserId() {
    this.userId = null;
    localStorage.removeItem('current_user_id');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };
    const userId = this.getUserId();
    if (userId) headers['X-User-ID'] = userId;

    try {
      const response = await fetch(url, { ...options, headers });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 404) {
          if (endpoint.includes('/bsl') || endpoint.includes('/leaderboard') || endpoint.includes('/admin') || endpoint.includes('/events')) {
            throw new ApiError(404, 'This feature is not available yet. It will be enabled in a later phase.');
          }
        }
        
        throw new ApiError(response.status, errorData.detail || `HTTP ${response.status}`);
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(0, 'We\'re having trouble connecting to the server. Please try again later.');
      }
      throw error;
    }
  }

  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body: data ? JSON.stringify(data) : undefined });
  }

  put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body: data ? JSON.stringify(data) : undefined });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Component compatibility methods
  createPool(payload: CreatePoolInput): Promise<Pool> {
    return this.post('/pools', payload);
  }

  joinPool(poolId: string, _body: { user_id: string }): Promise<PoolMembership> {
    return this.post(`/pools/${poolId}/join`);
  }

  leavePool(poolId: string): Promise<void> {
    return this.post(`/pools/${poolId}/leave`);
  }
}

// =============================================================================
// API Error Class
// =============================================================================

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// =============================================================================
// API Client Instance
// =============================================================================

export const api = new ApiClient(config.apiBaseUrl);

// =============================================================================
// Pool Service
// =============================================================================

export const PoolService = {
  getAll: (): Promise<Pool[]> => api.get('/pools'),
  getById: (poolId: string): Promise<Pool> => api.get(`/pools/${poolId}`),
  create: (input: CreatePoolInput): Promise<Pool> => api.post('/pools', input),
  update: (poolId: string, input: UpdatePoolInput): Promise<Pool> => api.put(`/pools/${poolId}`, input),
  deactivate: (poolId: string): Promise<Pool> => api.put(`/pools/${poolId}/status`, { status: 'cancelled' }),
  join: (poolId: string, userId: string = localStorage.getItem('current_user_id') || 'anonymous'): Promise<PoolMembership> => api.post(`/pools/${poolId}/join?user_id=${userId}`),
  leave: (poolId: string): Promise<void> => api.post(`/pools/${poolId}/leave`),
};

// =============================================================================
// Membership Service
// =============================================================================

export const MembershipService = {
  getCurrentUserMembership: (poolId: string): Promise<PoolMembership> => api.get(`/pools/${poolId}/membership`),
  getMembership: (poolId: string, userId: string): Promise<PoolMembership> => api.get(`/pools/${poolId}/members/${userId}`),
  getPoolMembers: (poolId: string): Promise<PoolMembership[]> => api.get(`/pools/${poolId}/members`),
  getLeaderboard: (poolId: string): Promise<LeaderboardEntry[]> => api.get(`/pools/${poolId}/leaderboard`),
};

// =============================================================================
// BSL Service
// =============================================================================

export const BSLService = {
  getProfile: (poolId: string, userId: string): Promise<BSLProfile> => api.get(`/pools/${poolId}/members/${userId}/bsl`),
  getPoolSummary: (poolId: string): Promise<unknown> => api.get(`/pools/${poolId}/bsl/summary`),
};



// =============================================================================
// Admin Service
// =============================================================================

export const AdminService = {
  adjustBalance: (poolId: string, userId: string, input: BalanceAdjustmentInput): Promise<PoolMembership> =>
    api.post(`/pools/${poolId}/bankroll/adjust`, { user_id: userId, ...input }),
  updatePoolStatus: (poolId: string, status: string): Promise<Pool> =>
    api.put(`/pools/${poolId}/status`, { status }),
};

// =============================================================================
// User Pools Helper
// =============================================================================

export async function fetchUserPoolsWithMemberships(): Promise<{ pool: Pool; membership: PoolMembership }[]> {
  const pools = await PoolService.getAll();
  const userPools: { pool: Pool; membership: PoolMembership }[] = [];
  for (const pool of pools) {
    try {
      const membership = await MembershipService.getCurrentUserMembership(pool.pool_id);
      userPools.push({ pool, membership });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) continue;
      throw error;
    }
  }
  return userPools;
}

// =============================================================================
// Phase 2C-2: Event Service
// =============================================================================

export const EventService = {
  getAll: (poolId: string): Promise<Event[]> => api.get(`/pools/${poolId}/events`),
  getById: (poolId: string, eventId: string): Promise<Event> => api.get(`/pools/${poolId}/events/${eventId}`),
};

// =============================================================================
// Phase 2C-2: Prediction Service
// =============================================================================

export const PredictionService = {
  getUserPredictions: (userId: string, poolId?: string): Promise<Prediction[]> => {
    const params = poolId ? `?pool_id=${poolId}` : '';
    return api.get(`/users/${userId}/predictions${params}`);
  },
  getById: (predictionId: string): Promise<Prediction> => api.get(`/predictions/${predictionId}`),
  create: (data: CreatePredictionRequest): Promise<Prediction> => api.post('/predictions', data),
};

// =============================================================================
// Phase 2C-2: Portfolio Service (GM Mode)
// =============================================================================

export const PortfolioService = {
  getUserPortfolios: (userId: string, poolId?: string): Promise<Portfolio[]> => {
    const params = poolId ? `?pool_id=${poolId}` : '';
    return api.get(`/users/${userId}/portfolios${params}`);
  },
  getById: (portfolioId: string): Promise<Portfolio> => api.get(`/portfolios/${portfolioId}`),
  create: (data: CreatePortfolioRequest): Promise<Portfolio> => api.post('/portfolios', data),
};

// =============================================================================
// Phase 2C-2: User Memberships
// =============================================================================

export const UserService = {
  getMemberships: (userId: string): Promise<PoolMembership[]> => api.get(`/users/${userId}/memberships`),
  getMe: (): Promise<{ user_id: string; is_admin: boolean }> => api.get('/users/me'),
};

// =============================================================================
// Admin Stats Service
// =============================================================================

export const AdminStatsService = {
  getStats: (): Promise<AdminStats> => api.get('/admin/stats'),
};

// =============================================================================
// Admin Pool Service (Phase 3H-A - Fixed)
// =============================================================================

// API response type (what backend actually returns - camelCase)
interface AdminPoolApiPool {
  id: number;
  poolId: string;
  name: string;
  domain: string;
  mode: string;
  status: string;
  participants: number;
  maxParticipants: number;
  bankroll: number;
  startDate: string | null;
  endDate: string | null;
  lockTime: string | null;
  createdAt: string | null;
}

interface AdminPoolApiResponse {
  pools: AdminPoolApiPool[];
  count: number;
}

// Transform backend camelCase to frontend snake_case
function transformAdminPool(apiPool: AdminPoolApiPool): AdminPool {
  return {
    pool_id: apiPool.poolId,
    name: apiPool.name,
    domain: apiPool.domain,
    mode: apiPool.mode,
    status: apiPool.status,
    member_count: apiPool.participants || 0,
    total_bankroll: apiPool.bankroll || 0,
    created_at: apiPool.createdAt || '',
  };
}

export const AdminPoolService = {
  getAll: async (params?: {
    status?: string;
    domain?: string;
    mode?: string;
    limit?: number;
    offset?: number;
  }): Promise<AdminPoolListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.set('status', params.status);
    if (params?.domain) queryParams.set('domain', params.domain);
    if (params?.mode) queryParams.set('mode', params.mode);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    const query = queryParams.toString();
    
    // Fetch and transform response
    const response = await api.get<AdminPoolApiResponse>(`/admin/pools${query ? `?${query}` : ''}`);
    
    // Map camelCase to snake_case and count to total
    return {
      pools: response.pools.map(transformAdminPool),
      total: response.count,
      limit: params?.limit || 20,
      offset: params?.offset || 0,
    };
  },

  create: (data: AdminCreatePoolRequest): Promise<AdminPool> =>
    api.post('/admin/pools', data),

  update: (poolId: string, data: AdminUpdatePoolRequest): Promise<AdminPool> =>
    api.put(`/admin/pools/${poolId}`, data),

  lock: (poolId: string, data?: LockPoolRequest): Promise<AdminPool> =>
    api.post(`/admin/pools/${poolId}/lock`, data || {}),

  delete: (poolId: string, hardDelete: boolean = false): Promise<{ success: boolean; poolId: string; hardDelete: boolean }> =>
    api.delete(`/admin/pools/${poolId}?hard_delete=${hardDelete}`),

  populateProps: (poolId: string | number, domain: string = 'nfl'): Promise<{ props_created: number; props_updated: number; events_created: number; players_created: number; message?: string }> =>
    api.post(`/admin/slates/${domain}/default/ingest`, { pool_id: poolId }),

  loadTestData: (domain: string = 'nfl'): Promise<{ success: boolean; count: number; message: string }> =>
    api.post(`/admin/props/load-test-data?domain=${domain}`, {}),

  getCacheStatus: (domain: string = 'nfl'): Promise<{ status: string; propCount: number; playerCount: number; teamCount: number; oldestEntry: string | null; newestEntry: string | null }> =>
    api.get(`/admin/props/cache-status?domain=${domain}`),

  syncFromVendor: (domain: string = 'nfl'): Promise<{ success: boolean; count: number; message: string }> =>
    api.post(`/admin/props/sync?domain=${domain}`, {}),
  ingestResults: (poolId: string): Promise<{ success: boolean; message: string; events_processed?: number; total_updated?: number }> =>
    api.post(`/admin/pools/${poolId}/ingest-results`),

  resolvePool: (poolId: string): Promise<{ success: boolean; message: string; e4_2_resolution?: object; e4_3_scoring?: object }> =>
    api.post(`/admin/pools/${poolId}/resolve`),
};

// =============================================================================
// Admin Membership Service
// =============================================================================

export const AdminMembershipService = {
  getAll: (params?: {
    pool_id?: string;
    user_id?: string;
    is_depleted?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<AdminMembershipListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.pool_id) queryParams.set('pool_id', params.pool_id);
    if (params?.user_id) queryParams.set('user_id', params.user_id);
    if (params?.is_depleted !== undefined) queryParams.set('is_depleted', params.is_depleted.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    const query = queryParams.toString();
    return api.get(`/admin/memberships${query ? `?${query}` : ''}`);
  },

  adjustBalance: (membershipId: number, data: AdjustBalanceRequest): Promise<AdjustBalanceResponse> =>
    api.put(`/admin/memberships/${membershipId}/balance`, data),

  remove: (membershipId: number, data: RemoveMemberRequest): Promise<RemoveMemberResponse> =>
    api.delete(`/admin/memberships/${membershipId}?reason=${encodeURIComponent(data.reason)}`),
};

// =============================================================================
// Audit Log Service
// =============================================================================

export const AuditLogService = {
  getAll: (params?: {
    admin?: string;
    action?: string;
    target_type?: string;
    target_id?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuditLogListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.admin) queryParams.set('admin', params.admin);
    if (params?.action) queryParams.set('action', params.action);
    if (params?.target_type) queryParams.set('target_type', params.target_type);
    if (params?.target_id) queryParams.set('target_id', params.target_id);
    if (params?.start_date) queryParams.set('start_date', params.start_date);
    if (params?.end_date) queryParams.set('end_date', params.end_date);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    const query = queryParams.toString();
    return api.get(`/admin/audit-log${query ? `?${query}` : ''}`);
  },

  getById: (auditId: number): Promise<AuditLogEntry> =>
    api.get(`/admin/audit-log/${auditId}`),

  getActionTypes: (): Promise<string[]> =>
    api.get('/admin/audit-log/actions'),
};

// =============================================================================
// Phase 2D-2: Pick & GM Mode Service
// =============================================================================

export const PickGMService = {
  // Pick Mode
  getPickSlate: (poolId: string): Promise<PickSlateResponse> =>
    api.get(`/pools/${poolId}/pick/slate`),
  
  submitPick: (poolId: string, data: SubmitPickRequest): Promise<PickResponse> =>
    api.post(`/pools/${poolId}/pick`, data),
  
  getPick: (poolId: string, predId: string): Promise<PickResponse> =>
    api.get(`/pools/${poolId}/pick/${predId}`),

  // GM Mode
  getGMSlate: (poolId: string): Promise<GMSlateResponse> =>
    api.get(`/pools/${poolId}/gm/slate`),
  
  submitPortfolio: (poolId: string, data: SubmitPortfolioRequest): Promise<PortfolioResponse> =>
    api.post(`/pools/${poolId}/gm/portfolio`, data),
  
  getPortfolio: (poolId: string, portfolioId: string): Promise<PortfolioResponse> =>
    api.get(`/pools/${poolId}/gm/portfolio/${portfolioId}`),
};

// =============================================================================
// Phase 2C-4: Behavior Analytics Service
// =============================================================================

export interface BehaviorSummary {
  pool_id: string;
  total_events: number;
  unique_users: number;
  avg_decision_time: number;
  avg_stake_percentage: number;
  decision_time_curve: { bins: number[]; values: number[] };
  stake_distribution: { buckets: string[]; counts: number[] };
  loss_chasing_prevalence: number;
}

export interface UserBehavior {
  user_id: string;
  pool_id: string;
  total_stake_events: number;
  avg_decision_time_seconds: number;
  avg_stake_percentage: number;
  loss_chasing_index: number;
  loss_chasing_events: number;
  current_streak: number;
  overall_accuracy: number;
}

export interface ChartData {
  pool_id: string;
  user_id?: string;
  bins?: number[];
  labels?: string[];
  values?: number[];
  buckets?: string[];
  counts?: number[];
}

export const BehaviorService = {
  getSummary: (poolId: string): Promise<BehaviorSummary> =>
    api.get(`/admin/behavior/${poolId}/summary`),

  getPoolUsers: (poolId: string, params?: { limit?: number; offset?: number }): Promise<UserBehavior[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    const query = queryParams.toString();
    return api.get(`/admin/behavior/${poolId}/users${query ? `?${query}` : ''}`);
  },

  getUserBehavior: (poolId: string, userId: string): Promise<UserBehavior> =>
    api.get(`/admin/behavior/${poolId}/user/${userId}`),

  getDecisionTimeData: (poolId: string, userId?: string): Promise<ChartData> => {
    const query = userId ? `?user_id=${userId}` : '';
    return api.get(`/admin/behavior/${poolId}/decision-time${query}`);
  },

  getStakeIntensityData: (poolId: string, userId?: string): Promise<ChartData> => {
    const query = userId ? `?user_id=${userId}` : '';
    return api.get(`/admin/behavior/${poolId}/stake-intensity${query}`);
  },

  getLockPressureData: (poolId: string, userId?: string): Promise<ChartData> => {
    const query = userId ? `?user_id=${userId}` : '';
    return api.get(`/admin/behavior/${poolId}/lock-pressure${query}`);
  },
};
